import {
  Component,
  computed,
  ElementRef,
  EventEmitter,
  Inject,
  inject,
  InjectionToken,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { LoaService } from '../../shared/loa.service';
import {
  debounceTime,
  distinctUntilChanged,
  finalize,
} from 'rxjs/operators';
import { lastValueFrom, Subject, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { LoaDeleteImpact, LoaListItem } from '../../shared/loa.model';
import { AlertService } from 'src/app/core/services/alert.service';
import { DropdownService } from '../../../../shared/service/dropdown.service';
import { SpinnerService } from '../../../../core/services/spinner.service';
import { isEmptyAfterDelete } from 'src/app/shared/utils/pagination';
import { ActivatedRoute, Params, Router } from '@angular/router';
import {
  DropdownItem,
  Nullable,
  Pagination,
} from '@shared/models/common.model';
import { ActivatedQueryParamsService } from '@shared/service/activated-queryparams.service';
import { UtilsService } from '@core/services/utils.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DrfList } from '@shared/models/drf-response.models';
import { verticalCollapse } from '@shared/animations';
import { Store } from '@ngxs/store';
import { ErrorNotification } from '@store/memo/memo.actions';

@Component({
  selector: 'app-loa',
  templateUrl: './loa.component.html',
  styleUrls: ['./loa.component.scss'],
  providers: [ActivatedQueryParamsService],
  animations: [verticalCollapse],
})
export class LoaComponent implements OnInit, OnDestroy {
  @Output() closed = new EventEmitter();

  debouncer = new Subject<string>();
  userChoices = signal<DropdownItem[]>([]);
  budgetCategoryChoices = signal<DropdownItem<number>[]>([]);
  readonly isOverBudgetChoices = [
    {
      label: 'BUDGET-LOA.Amount',
      value: 'false',
    },
    {
      label: 'BUDGET-LOA.Over budget / No budget',
      value: 'true',
    },
  ];

  @ViewChild('confirmDeleteModal')
  confirmDeleteModal: ElementRef;
  @ViewChild('deleteInUseModal')
  deleteInUseModal: ElementRef;

  loaList = signal<LoaListItem[]>([]);
  isLoading = signal<boolean>(false);
  count = signal<number>(0);
  isEmpty = signal<boolean>(true);
  searchNotFound = computed(() => this.loaList().length === 0);
  lang = signal<string>('en');
  targetLoa: LoaListItem;
  deleteInUseSubtitle = '';
  isShowFilter = signal<boolean>(false);
  subscription = new Subscription();
  sortChoices: DropdownItem<string>[] = [
    {
      label: 'LOA.Line of approval name',
      value: 'name',
    },
  ];
  // ordering = signal<string>('name');
  filters = signal<
    Nullable<{
      search: string;
      department_id: number;
      approver: number;
      category: number;
      is_over_budget: string;
      amount: number;
    }>
  >({});
  pagination = signal<Pagination>({
    page: 1,
    page_size: 10,
  });
  queryParams = computed<Params>(() => {
    const valid = this.utils.getValidQueryParams({
      ...this.filters(),
      ...this.pagination(),
      // ordering: this.ordering(),
      ordering: 'created_at',
    });
    return valid;
  });

  advanceSearchList =
    signal<DrfList<LoaListItem, any[]>['query_params_display']>(null);
  isInAdvanceSearchStage = computed<boolean>(() => {
    return (
      this.advanceSearchList()?.filter(
        (obj: any) => obj.key !== 'search',
      ).length !== 0
    );
  });
  isAdvSearchCollapsed = signal<boolean>(false);

  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private alert = inject(AlertService);
  private loaService = inject(LoaService);
  private spinner = inject(SpinnerService);
  private translate = inject(TranslateService);
  private dropdownService = inject(DropdownService);
  private modalService = inject(NgbModal);
  private store = inject(Store);
  protected activatedQueryParams = inject(
    ActivatedQueryParamsService,
  );
  protected utils = inject(UtilsService);

  constructor(
    @Optional()
    @Inject(LOA_FEATURE_CONFIG)
    public feature?: LoaFeatureConfig,
  ) {
    this.activatedQueryParams.addGroup('default', {
      search: { type: 'string' },
      department_id: { type: 'number' },
      approver: { type: 'number' },
      category: { type: 'number' },
      is_over_budget: { type: 'string' },
      amount: { type: 'number' },
    });
    this.lang.set(this.translate.currentLang);
    this.subscription.add(
      this.translate.onLangChange.subscribe((event) => {
        this.lang.set(event.lang);
      }),
    );
    this.debouncer
      .pipe(debounceTime(600), distinctUntilChanged())
      .subscribe(() => {
        this.filterChange();
      });
  }

  ngOnInit(): void {
    if (this.feature?.budgetLoa) {
      this.fetchBudgetCategoryChoices();
    }
    this.subscribeRoute();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  subscribeRoute(): void {
    const sub = this.activatedQueryParams.queryParamChanges.subscribe(
      {
        next: (queryParams) => {
          this.filters.set(queryParams.paramsByMap['default']);

          this.pagination.set(
            queryParams.paramsByMap['pagination'] as Pagination,
          );

          this.loadLOAList();
          this.initiateUserChoices();
        },
      },
    );
    this.subscription.add(sub);
  }

  initiateUserChoices(): void {
    const { approver } = this.filters();
    if (
      !approver ||
      this.userChoices().find((obj) => obj.value === approver)
    ) {
      return;
    }
    const subs = this.dropdownService
      .getDropdown(
        this.utils.getValidQueryParams({
          type: 'people',
          ids: approver,
        }),
      )
      .subscribe({
        next: (res) => {
          this.userChoices.set(res['people']);
        },
      });
    this.subscription.add(subs);
  }

  async fetchBudgetCategoryChoices() {
    try {
      const res = await lastValueFrom(
        this.dropdownService.getDropdown({ type: 'category' }),
      );
      this.budgetCategoryChoices.set(res['category']);
    } catch {
      this.budgetCategoryChoices.set([]);
    }
  }

  loadLOAList(): void {
    const params = this.queryParams();
    // if (this.lang() === 'en') {
    //   const ordering = params?.['ordering'];
    //   if (ordering?.includes('name')) {
    //     params['ordering'] = ordering.replace('name', 'name_en');
    //   }
    // }

    this.spinner.show();
    this.isLoading.set(true);
    const listApi$ = this.feature?.budgetLoa
      ? this.loaService.getBudgetLOAList(params)
      : this.loaService.getLOAList(params);
    listApi$
      .pipe(
        finalize(() => {
          this.spinner.hide();
          this.isLoading.set(false);
        }),
      )
      .subscribe({
        next: (res) => {
          const response = res as unknown as DrfList<
            LoaListItem,
            any[]
          >;
          this.isEmpty.set(response.no_data);
          if (response.results) {
            this.count.set(response.count);
            this.loaList.set(response.results);
            this.advanceSearchList.set(
              response?.query_params_display,
            );
          }
        },
        error: () => {
          this.loaList.set([]);
          this.count.set(0);
          this.isEmpty.set(true);
          this.advanceSearchList.set(null);
          this.alert.error('Unable to load loa list');
        },
      });
  }

  clearSearch(): void {
    this.filters.set({});
    this.userChoices.set([]);
    this.changePage(1);
  }

  filterChange(): void {
    this.changePage(1);
  }

  onSortChange() {
    this.updateQueryParamsByNavigation();
  }

  deleteLOA(): void {
    if (!this.targetLoa) {
      return;
    }
    const removeApi$ = this.feature?.budgetLoa
      ? this.loaService.removeBudgetLOA(this.targetLoa.id)
      : this.loaService.removeLOA(this.targetLoa.id);
    removeApi$.subscribe({
      next: () => {
        const currentPage = this.pagination().page;
        const params = {
          items: this.loaList(),
          totalItems: this.count(),
          pageSize: this.pagination().page_size,
          currentPage,
        };
        if (isEmptyAfterDelete(params)) {
          this.changePage(currentPage - 1);
          this.updateQueryParamsByNavigation();
        }
        this.alert.success(
          this.translate.instant('ALERT.Save successful'),
        );
        this.loadLOAList();
      },
      error: () => this.alert.error('Unable to load loa list'),
    });
  }

  changePage(page: number) {
    this.pagination.update((p) => {
      return { ...p, page };
    });
    this.updateQueryParamsByNavigation();
  }

  changePageSize(page_size: number) {
    this.pagination.update((p) => {
      return { ...p, page_size };
    });
    this.updateQueryParamsByNavigation();
  }

  changeToEditLoa(id: any) {
    this.router.navigate(['./' + id.toString()], {
      relativeTo: this.route,
      state: {
        backPath: this.router.url,
      },
    });
  }

  changeToCreateLoa(): void {
    this.router.navigate(['./', 'create-loa'], {
      relativeTo: this.route,
      state: {
        backPath: this.router.url,
      },
    });
  }

  importSuccess() {
    this.loadLOAList();
  }

  updateFilter(key: string, value: any) {
    if (this.feature?.budgetLoa) {
      if (key === 'is_over_budget' && value === 'true') {
        this.filters.update((f) => {
          const result = { ...f, is_over_budget: 'true' };
          delete result.amount;
          return result;
        });
        return;
      }
    }
    this.filters.update((f) => {
      return { ...f, [key]: value };
    });
  }

  onSelectApproverDropdown(value: DropdownItem | null) {
    if (value) {
      this.userChoices.set([value]);
    } else {
      this.userChoices.set([]);
    }
  }

  updateQueryParamsByNavigation(): void {
    this.router.navigate([], {
      queryParams: this.utils.getValidQueryParams(this.queryParams()),
    });
  }

  openDeleteModal(item: LoaListItem) {
    this.targetLoa = item;
    if (this.feature?.budgetLoa) {
      this.openConfirmDeleteModal(this.confirmDeleteModal);
      return;
    }
    this.spinner.show();
    this.loaService
      .checkDeleteLOA(item.id)
      .pipe(finalize(() => this.spinner.hide()))
      .subscribe({
        next: (impact) => {
          if (!impact.in_use) {
            this.openConfirmDeleteModal(this.confirmDeleteModal);
            return;
          }
          this.deleteInUseSubtitle =
            this.buildDeleteInUseSubtitle(impact);
          this.openConfirmDeleteModal(this.deleteInUseModal);
        },
        error: (error) =>
          this.store.dispatch(new ErrorNotification(error)),
      });
  }

  private openConfirmDeleteModal(modal: ElementRef) {
    this.modalService
      .open(modal, {
        centered: true,
      })
      .result.then(
        () => this.deleteLOA(),
        () => {},
      );
  }

  private buildDeleteInUseSubtitle(impact: LoaDeleteImpact): string {
    if (impact.template_count && impact.draft_memo_count) {
      return this.translate.instant(
        'LOA.IN-USE.SUB-TEMPLATE-AND-MEMO',
        {
          templates: impact.template_count,
          memos: impact.draft_memo_count,
        },
      );
    }
    if (impact.template_count) {
      return this.translate.instant('LOA.IN-USE.SUB-TEMPLATE', {
        templates: impact.template_count,
      });
    }
    return this.translate.instant('LOA.IN-USE.SUB-MEMO', {
      memos: impact.draft_memo_count,
    });
  }
}

export interface LoaFeatureConfig {
  budgetLoa?: boolean;
}

export const LOA_FEATURE_CONFIG =
  new InjectionToken<LoaFeatureConfig>('loa feature configuration');
